/**
 * ASC 606 Phase 11g — Progress Tracker Page
 * ==========================================
 *
 * Reviewer-facing surface for Phase 11 Step 5 operational work:
 *   • Contract + POB selectors
 *   • 25-27 satisfaction-method classifier runner (11a)
 *   • 55-18 R2I expedient toggle (11d) per POB
 *   • Cost-to-Cost tab (11b/11c): baseline + ledger + recognition
 *   • Milestones tab (11d): milestone confirmations + recognition
 *   • Units Delivered tab (11d): delivery logs + recognition
 *   • Estimate Revisions tab (11e): staged/consumed/cancelled timeline
 *
 * Backend surface used:
 *   GET  /revenue/contracts (contract list)
 *   GET  /revenue/contracts/{id}/satisfaction-method (11a state)
 *   POST /revenue/contracts/{id}/classify-satisfaction-method (11a run)
 *   (nested panels fetch their own endpoints — see each panel file)
 */

import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import CostLedgerPanel from '../components/ui/CostLedgerPanel';
import MilestoneConfirmPanel from '../components/ui/MilestoneConfirmPanel';
import UnitDeliveryPanel from '../components/ui/UnitDeliveryPanel';
import EstimateRevisionPanel from '../components/ui/EstimateRevisionPanel';
import RightToInvoicePanel from '../components/ui/RightToInvoicePanel';
import ContractCostPanel from '../components/ui/ContractCostPanel';

const API = `${API_V1}/revenue`;

interface ContractLite {
  id: number;
  contract_number: string;
  customer_name: string | null;
  status: string;
  entity_code: string | null;
}

interface PobLite {
  id: number;
  po_number: number;
  description: string;
  progress_method: string | null;
  over_time_qualified: boolean | null;
  over_time_criterion_met: string | null;
  right_to_invoice_expedient: boolean;
  right_to_invoice_rationale: string | null;
  pending_estimate_revision_amount: number;
}

interface SatisfactionResult {
  contract_id: number;
  contract_number: string;
  pob_count: number;
  over_time_count: number;
  pobs: Array<{
    pob_id: number;
    progress_method: string | null;
    over_time_qualified: boolean | null;
    over_time_criterion_met: string | null;
    classified_at: string | null;
  }>;
}

export default function ProgressTracker() {
  const [contracts, setContracts] = useState<ContractLite[]>([]);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [contractsErr, setContractsErr] = useState<string | null>(null);

  const [selectedContract, setSelectedContract] = useState<ContractLite | null>(null);
  const [pobs, setPobs] = useState<PobLite[]>([]);
  const [selectedPobId, setSelectedPobId] = useState<number | null>(null);
  const [pobsLoading, setPobsLoading] = useState(false);
  const [pobsErr, setPobsErr] = useState<string | null>(null);

  const [satisfaction, setSatisfaction] = useState<SatisfactionResult | null>(null);
  const [classifierBusy, setClassifierBusy] = useState(false);
  const [classifierFlash, setClassifierFlash] = useState<string | null>(null);

  const [tab, setTab] = useState<'cost_to_cost' | 'milestones' | 'units' | 'revisions' | 'contract_costs'>('cost_to_cost');
  const [costAssetsCount, setCostAssetsCount] = useState<number>(0);

  // Load contract list
  const loadContracts = useCallback(async () => {
    setContractsLoading(true);
    setContractsErr(null);
    try {
      const res = await authFetch(`${API}/contracts?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setContracts(data.contracts || data || []);
    } catch (e: any) {
      setContractsErr(e.message || String(e));
    } finally {
      setContractsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  // Load POBs + satisfaction state for selected contract
  const loadContractDetail = useCallback(async (contractId: number) => {
    setPobsLoading(true);
    setPobsErr(null);
    try {
      const [rcRes, satRes] = await Promise.all([
        authFetch(`${API}/contracts/${contractId}`),
        authFetch(`${API}/contracts/${contractId}/satisfaction-method`).catch(() => null),
      ]);
      if (!rcRes.ok) throw new Error(`contract HTTP ${rcRes.status}`);
      const rcData = await rcRes.json();
      const rcPobs: PobLite[] = (rcData.obligations || []).map((p: any) => ({
        id: p.id,
        po_number: p.po_number,
        description: p.description || p.obligation_name || `POB ${p.id}`,
        progress_method: p.progress_method || null,
        over_time_qualified: p.over_time_qualified ?? null,
        over_time_criterion_met: p.over_time_criterion_met || null,
        right_to_invoice_expedient: !!p.right_to_invoice_expedient,
        right_to_invoice_rationale: p.right_to_invoice_rationale || null,
        pending_estimate_revision_amount: Number(p.pending_estimate_revision_amount || 0),
      }));
      setPobs(rcPobs);
      if (rcPobs.length > 0 && !selectedPobId) {
        setSelectedPobId(rcPobs[0].id);
      }
      if (satRes && satRes.ok) {
        const sat = await satRes.json();
        setSatisfaction(sat);
      } else {
        setSatisfaction(null);
      }
      // Phase 12d — fetch capitalized cost assets count to drive tab badge + visibility
      try {
        const caRes = await authFetch(`${API}/contracts/${contractId}/acquisition-costs?limit=500`);
        if (caRes.ok) {
          const caData = await caRes.json();
          setCostAssetsCount((caData.assets || []).length);
        } else {
          setCostAssetsCount(0);
        }
      } catch {
        setCostAssetsCount(0);
      }
    } catch (e: any) {
      setPobsErr(e.message || String(e));
    } finally {
      setPobsLoading(false);
    }
  }, [selectedPobId]);

  useEffect(() => {
    if (selectedContract) {
      setSelectedPobId(null);
      loadContractDetail(selectedContract.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract]);

  const runClassifier = async () => {
    if (!selectedContract) return;
    setClassifierBusy(true);
    setClassifierFlash(null);
    try {
      const res = await authFetch(
        `${API}/contracts/${selectedContract.id}/classify-satisfaction-method`,
        { method: 'POST' },
      );
      if (res.ok) {
        const data = await res.json();
        setClassifierFlash(
          `Classifier ran: ${data.over_time_count}/${data.pob_count} POB(s) over-time.`,
        );
        await loadContractDetail(selectedContract.id);
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        setClassifierFlash(`Classifier failed: ${detail}`);
      }
    } catch (e: any) {
      setClassifierFlash(`Classifier error: ${e?.message || 'Network error'}`);
    } finally {
      setClassifierBusy(false);
    }
  };

  const selectedPob = pobs.find((p) => p.id === selectedPobId) || null;
  const contractIsActive = (selectedContract?.status || '').toUpperCase() === 'ACTIVE';

  return (
    <PageLayout title="Progress Tracker" subtitle="ASC 606 Step 5 — recognition engine, milestones, deliveries, revisions">
      {/* Contract picker */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
          Contract
        </label>
        {contractsErr && <div style={{ color: 'var(--negative)', fontSize: 12, marginBottom: 6 }}>{contractsErr}</div>}
        <select
          aria-label="Select contract"
          value={selectedContract?.id || ''}
          onChange={(e) => {
            const id = parseInt(e.target.value, 10);
            const c = contracts.find((x) => x.id === id) || null;
            setSelectedContract(c);
          }}
          disabled={contractsLoading}
          style={{
            width: '100%',
            maxWidth: 600,
            padding: 8,
            fontSize: 13,
            border: '1px solid var(--border-subtle)',
            borderRadius: 4,
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">— select contract —</option>
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.contract_number} · {c.customer_name || '-'} · {c.status}
            </option>
          ))}
        </select>
      </div>

      {selectedContract && (
        <>
          {/* POB picker + classifier */}
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 300 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Performance Obligation
                </label>
                {pobsErr && <div style={{ color: 'var(--negative)', fontSize: 12, marginBottom: 6 }}>{pobsErr}</div>}
                <select
                  aria-label="Select performance obligation"
                  value={selectedPobId || ''}
                  onChange={(e) => setSelectedPobId(parseInt(e.target.value, 10) || null)}
                  disabled={pobsLoading || pobs.length === 0}
                  style={{
                    width: '100%',
                    padding: 8,
                    fontSize: 13,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">— select POB —</option>
                  {pobs.map((p) => (
                    <option key={p.id} value={p.id}>
                      #{p.po_number} · {p.description.slice(0, 80)}
                      {p.progress_method ? ` · ${p.progress_method}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  type="button"
                  onClick={runClassifier}
                  disabled={classifierBusy || !selectedContract || contractIsActive}
                  style={{
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    background: classifierBusy || contractIsActive ? 'var(--bg-hover)' : 'var(--accent)',
                    color: classifierBusy || contractIsActive ? 'var(--text-hint)' : 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: classifierBusy ? 'wait' : contractIsActive ? 'not-allowed' : 'pointer',
                  }}
                >
                  {classifierBusy ? 'Classifying...' : 'Run 25-27 Classifier'}
                </button>
                {satisfaction && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {satisfaction.over_time_count}/{satisfaction.pob_count} over-time POBs
                  </div>
                )}
              </div>
            </div>
            {classifierFlash && (
              <div
                style={{
                  padding: 8,
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid var(--positive)',
                  borderRadius: 4,
                  fontSize: 12,
                  color: 'var(--positive)',
                }}
              >
                {classifierFlash}
              </div>
            )}
            {selectedPob && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                  padding: 10,
                  background: 'var(--bg-surface)',
                  borderRadius: 4,
                }}
              >
                <Chip label="Progress Method" value={selectedPob.progress_method || '—'} />
                <Chip
                  label="Over-time Qualified"
                  value={
                    selectedPob.over_time_qualified == null
                      ? 'Unclassified'
                      : selectedPob.over_time_qualified
                      ? `Yes (25-27${selectedPob.over_time_criterion_met ? `(${selectedPob.over_time_criterion_met})` : ''})`
                      : 'No (PIT)'
                  }
                />
                <Chip
                  label="R2I Expedient"
                  value={selectedPob.right_to_invoice_expedient ? '55-18 ENABLED' : 'Disabled'}
                />
                <Chip
                  label="Pending Revision"
                  value={
                    selectedPob.pending_estimate_revision_amount === 0
                      ? '—'
                      : `$${selectedPob.pending_estimate_revision_amount.toLocaleString()}`
                  }
                  warn={selectedPob.pending_estimate_revision_amount !== 0}
                />
              </div>
            )}
          </div>

          {/* R2I panel — inline (per POB) */}
          {selectedPob && (
            <div style={{ marginBottom: 16 }}>
              <RightToInvoicePanel
                contractId={selectedContract.id}
                pobId={selectedPob.id}
                currentEnabled={selectedPob.right_to_invoice_expedient}
                currentRationale={selectedPob.right_to_invoice_rationale}
                contractStatus={selectedContract.status}
                onUpdated={() => loadContractDetail(selectedContract.id)}
              />
            </div>
          )}

          {/* Tabs */}
          <TabBar
            tabs={[
              { key: 'cost_to_cost', label: 'Cost-to-Cost' },
              { key: 'milestones', label: 'Milestones' },
              { key: 'units', label: 'Units Delivered' },
              { key: 'revisions', label: 'Estimate Revisions' },
              { key: 'contract_costs', label: 'Contract Costs (340-40)', count: costAssetsCount || undefined },
            ]}
            active={tab}
            onChange={(k) => setTab(k as any)}
          />

          {selectedPob ? (
            <>
              {tab === 'cost_to_cost' && (
                <CostLedgerPanel
                  contractId={selectedContract.id}
                  pobId={selectedPob.id}
                  contractStatus={selectedContract.status}
                  onStateChanged={() => loadContractDetail(selectedContract.id)}
                />
              )}
              {tab === 'milestones' && (
                <MilestoneConfirmPanel
                  contractId={selectedContract.id}
                  pobId={selectedPob.id}
                  onStateChanged={() => loadContractDetail(selectedContract.id)}
                />
              )}
              {tab === 'units' && (
                <UnitDeliveryPanel
                  contractId={selectedContract.id}
                  pobId={selectedPob.id}
                  onStateChanged={() => loadContractDetail(selectedContract.id)}
                />
              )}
              {tab === 'revisions' && (
                <EstimateRevisionPanel
                  contractId={selectedContract.id}
                  pobId={selectedPob.id}
                  contractStatus={selectedContract.status}
                  onStateChanged={() => loadContractDetail(selectedContract.id)}
                />
              )}
              {tab === 'contract_costs' && (
                <ContractCostPanel
                  contractId={selectedContract.id}
                  contractStatus={selectedContract.status}
                  pobs={pobs.map((p) => ({ id: p.id, po_number: p.po_number, description: p.description }))}
                  onStateChanged={() => loadContractDetail(selectedContract.id)}
                />
              )}
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
              Select a POB to view progress data.
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
}

function Chip({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)' }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: warn ? 'var(--warning)' : 'var(--text-primary)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}
